import ts, { SymbolFlags, isArrayTypeNode } from 'typescript';
import { createSystem, createVirtualCompilerHost, createDefaultMapFromCDN } from "@typescript/vfs";
// import fetch from 'node-fetch';
//import { readFileSync, writeFileSync } from 'fs';
import { Node } from "./nodes"

type ParserProgramOptions = {
  macrosEnabled?: boolean,
  macroEditor?: boolean,
};

export class Parser {
  programOptions: ParserProgramOptions; 
  options: ts.CompilerOptions;
  root: string;
  constructor(root: string, programOptions?: ParserProgramOptions) {
    this.root = root;
    this.programOptions = programOptions ?? {};
    this.options = {
      "strict": true,
      "noImplicitAny": true,
      "strictNullChecks": true,
      "strictFunctionTypes": true,
      "strictPropertyInitialization": true,
      "strictBindCallApply": true,
      "noImplicitThis": true,
      "noImplicitReturns": true,
      "noUncheckedIndexedAccess": false,
      "useDefineForClassFields": false,
      "alwaysStrict": true,
      "allowUnreachableCode": false,
      "allowUnusedLabels": false,
      "downlevelIteration": false,
      "noEmitHelpers": false,
      "noLib": false,
      "noStrictGenericChecks": false,
      "noUnusedLocals": false,
      "noUnusedParameters": false,
      "esModuleInterop": true,
      "preserveConstEnums": false,
      "removeComments": false,
      "skipLibCheck": false,
      "checkJs": false,
      "allowJs": false,
      "declaration": true,
      "importHelpers": false,
      "experimentalDecorators": true,
      "emitDecoratorMetadata": true,
      "moduleResolution": 2,
      "target": 4,
      "jsx": 2,
      "module": 99
    };

    /*
        if (process.env.NODE_ENV) {
            const LocalStorage = require('node-localstorage').LocalStorage;
            global.localStorage = new LocalStorage('./cache');
        }
        */
  }

  async initWithFile(file: string) {
    //const code = readFileSync(file).toString();
    this.initWithContent('');//code);
  }

  async initWithContent(content: string) {
    content = (content || '').replace(/Distinct\<T\> \= T/g, 'Distinct<T> = T & { __distro_tagged: true }');

    if (this.programOptions.macroEditor) {
      // bit of a hack to conditionally update types for macro editor ... but its simple.
      content = content.replace('(Section & CommonProperties)[], // & MacroBlockProperties // for Subsurvey Sections', '(Section & CommonProperties & MacroBlockProperties)[], // for Subsurvey Sections');
      content = content.replace('(ValueExpr | NamedExpression | NotificationSender)[], // & MacroBlockProperties // for ApplicantTable columns.', '((ValueExpr | NamedExpression | NotificationSender) & MacroBlockProperties)[], // for ApplicantTable columns');
      content = content.replace(/; \/\/ & MacroBlockProperties/g, ' & MacroBlockProperties;');
      content = content.replace(/\/\/ & MacroBlockProperties/g, ' & MacroBlockProperties');
    }

    console.log("Getting map from CDN", content.slice(0, 300))
    // This allows us to find the Modern/Legacy types and handle them easier.
    // Note: an extra '_' is automatically prepended to the names, so they end up as '___distro_modern' and '___distro_legacy'
    content = content.replace('type WithLegacy<Modern, Legacy> = Modern | Legacy;', 'type WithLegacy<Modern, Legacy> = { __distro_modern: Modern, __distro_legacy: Legacy };');

    const fsMap = await createDefaultMapFromCDN(
      this.options,
      ts.version,
      true,
      ts,
      undefined,
      fetch as any,
      undefined //global.localStorage
    );

    fsMap.set("/index.ts", content)

    const system = createSystem(fsMap);
    const host = createVirtualCompilerHost(system, this.options, ts)
    const program = ts.createProgram({
      rootNames: [...fsMap.keys()],
      options: this.options,
      host: host.compilerHost,
    });

    const types = program.getTypeChecker();

    const index = program.getSourceFile('/index.ts');

    //console.log('index', index);

    let sym = types.getSymbolAtLocation(index!);

    //console.log('symbol', sym);
    const exports = types.getExportsOfModule(sym as any);

    let root: ts.Symbol | null = null;
    for (const e of exports) {
      if (e.escapedName === this.root) {
        root = e;
      }
    }

    if (!root || !root.declarations) {
      throw new Error("Root not found");
    }

    const t = root.declarations[0] as ts.TypeAliasDeclaration;
    if (ts.isTypeAliasDeclaration(t)) {
      //console.log(t.kind, t.type, ts.SyntaxKind.TypeAliasDeclaration);

      const type = types.getTypeFromTypeNode(t.type);

      const toExplore: [string, any][] = [];

      function explore(t: ts.Type, doExplore?: string): Node {
        switch (t.getFlags()) {
          // TODO: Disambiguate array
          case ts.TypeFlags.Object: {
            const props = t.getProperties();

            let meta: Record<string, string> = {};
            let propMeta: Record<string, Record<string, any>> = {};
            let jsDoc = (t.aliasSymbol?.declarations?.[0] as any)?.jsDoc;
            if (jsDoc?.length > 0) {
              if(jsDoc[0].comment) {
                meta.comment = jsDoc[0].comment;
              }
              jsDoc[0].tags?.map((t: any) => {
                meta[t.tagName.text] = t.comment;
              })
            }
            let start = t.aliasSymbol?.declarations?.[0].pos;
            let end = t.aliasSymbol?.declarations?.[0].end;

            if (t.getProperty('kind') && t.aliasSymbol) {
              meta.distroKind = t.aliasSymbol.name;
            }

            // Array types are conceptualized as Array objects w/ a type param
            if ((t as ts.TypeReference).target?.symbol?.escapedName === 'Array') {
              return {
                kind: 'array',
                element: explore((t as any).resolvedTypeArguments[0]),
                source: content.slice(start, end),
              }
            }

            const toReturn: {
              name?: string,
              kind: 'object',
              props: Record<string, any>,
              required: string[],
              meta?: Record<string, any>,
              propMeta?: Record<string, any>,
              source?: string
            } = {
              kind: 'object',
              props: {},
              required: [],
              meta,
              propMeta,
              // Prevent the entire source being copied around if we don't have an aliasSymbol
              source: (start && end) ? content.slice(start, end) : ''
            };

            if (t.aliasSymbol?.escapedName && t.aliasSymbol?.escapedName !== doExplore) {
              toExplore.push([t.aliasSymbol.escapedName, t]);
              return {
                kind: 'ref',
                label: t.aliasSymbol.escapedName,
                source: content.slice(start, end),
              }
            }

            let modernType: ts.Type | undefined;
            let legacyType: ts.Type | undefined;

            for (const p of props) {
              if (!types.isOptionalParameter((p as any).declarations[0])) {
                toReturn.required.push(p.escapedName as string)
              }

              let jsDoc = (p.declarations?.[0] as any)?.jsDoc;
              if (jsDoc?.length > 0) {
                propMeta[p.escapedName as string] = {};
                if(jsDoc[0].comment) {
                  propMeta[p.escapedName as string].comment = jsDoc[0].comment;
                }
                jsDoc[0].tags?.map((t: any) => {
                  propMeta[p.escapedName as string][t.tagName.text] = t.comment;
                })
              }

              const t2 = (p as any).declarations[0] as ts.TypeAliasDeclaration;
              let type = types.getTypeFromTypeNode(t2.type)
              // If this is a type parameter we need to ask TS to resolve the type from the symbol
              if (type.flags === ts.TypeFlags.TypeParameter) {
                type = types.getTypeOfSymbolAtLocation(p, p.declarations![0]);
              }

              // If we find the modern or legacy type, save it to be specially handled later
              if (p.escapedName == '___distro_modern') {
                modernType = type;
              }
              if (p.escapedName == '___distro_legacy') {
                legacyType = type;
              }

              toReturn.props[p.escapedName as string] = explore(type);

              // Update the child props if it's not an object
              if (typeof toReturn.props[p.escapedName as string] === 'object') {
                toReturn.props[p.escapedName as string].meta = {
                  ...(toReturn.props[p.escapedName as string].meta || {}),
                  ...propMeta[p.escapedName as string]
                }
              }
            }

            if (modernType && legacyType) {
              const exploredLegacy = explore(legacyType);
              return {
                kind: 'union' as 'union',
                options: [explore(modernType), { ...exploredLegacy, meta: { ...exploredLegacy.meta, isLegacy: true }}],
                // source: content.slice(start, end),
              };
            } 
                        
            return toReturn;
          }
          case ts.TypeFlags.String: {
            return {
              kind: 'string',
            }
          }
          case ts.TypeFlags.Number: {
            return {
              kind: 'number',
            }
          }
          case ts.TypeFlags.Boolean | ts.TypeFlags.Union: {
            return {
              kind: 'boolean',
            }
          }
          // TODO: Check if this is set
          case ts.TypeFlags.Union: {
            if (t.aliasSymbol?.escapedName && t.aliasSymbol?.escapedName !== doExplore) {
              let start = t.aliasSymbol?.declarations?.[0].pos;
              let end = t.aliasSymbol?.declarations?.[0].end;

              toExplore.push([t.aliasSymbol.escapedName, t]);
              return {
                kind: 'ref',
                label: t.aliasSymbol.escapedName,
                source: content.slice(start, end),
              }
            }

            let meta: Record<string, string> = {};
            let jsDoc = (t.aliasSymbol?.declarations?.[0] as any)?.jsDoc;
            if (jsDoc?.length > 0) {
              if(jsDoc[0].comment) {
                meta.comment = jsDoc[0].comment;
              }
              jsDoc[0].tags?.map((t: any) => {
                meta[t.tagName.text] = t.comment;
              })
            }
            // let start = t.aliasSymbol?.declarations?.[0].pos;
            // let end = t.aliasSymbol?.declarations?.[0].end;

            return {
              kind: 'union',
              options: (t as unknown as ts.UnionTypeNode).types.map(explore as any),
              meta,
              // source: content.slice(start, end),
            }
          }
          case ts.TypeFlags.StringLiteral:
          case ts.TypeFlags.NumberLiteral:
          case ts.TypeFlags.BooleanLiteral:
          {
            return {
              kind: 'literal',
              value: (t as any).value,
            }
          }
          case ts.TypeFlags.Any:
          {
            //console.log(t)
            break;
          }
          case ts.TypeFlags.Intersection:
          {
            let meta: Record<string, string> = {};
            let jsDoc = (t.aliasSymbol?.declarations?.[0] as any)?.jsDoc;
            if (jsDoc?.length > 0) {
              if(jsDoc[0].comment) {
                meta.comment = jsDoc[0].comment;
              }
              jsDoc[0].tags?.map((t: any) => {
                meta[t.tagName.text] = t.comment;
              })
            }

            let start = t.aliasSymbol?.declarations?.[0].pos;
            let end = t.aliasSymbol?.declarations?.[0].end;

            const intersection = {
              kind: 'intersection' as 'intersection',
              constraints: (t as unknown as ts.UnionTypeNode).types.map<Node>(explore as any),
              meta,
              source: content.slice(start, end)
            };

            // Special handling for our Distinct<T> type
            if (intersection.constraints.length === 2) {
              if (intersection.constraints[0].kind === 'object' && intersection.constraints[0].props.___distro_tagged) {
                intersection.constraints[1].meta = meta;
                return intersection.constraints[1];
              }
              if (intersection.constraints[1].kind === 'object' && intersection.constraints[1].props.___distro_tagged) {
                intersection.constraints[0].meta = meta;
                return intersection.constraints[0];
              }
            }

            return intersection;
          }
          default: {
            console.log("UNKNOWN", t.getFlags());
          }
        }
        return { kind: 'TBD', type: SymbolFlags[t.getFlags()] } as any;
      }
      const root = explore(type, this.root);
      const refs: Record<string, any> = {
        [this.root]: root
      };
      while (toExplore.length) {
        const [ref, t] = toExplore.pop()!;
        if (refs[ref]) continue;
        refs[ref] = explore(t, ref);
      }       

      return refs;

      /*
            writeFileSync('./src/example/schema_parsed.ts', `
        export default ${JSON.stringify(refs, null, 2)};
        `)
            */
    } else {
      console.log("Root is not a type alias");
    }

  }
}

if (process.env.NODE_ENV) {
}
